import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Upload, X, Edit2, Trash2, Filter, Cpu } from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { resourceService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import ConfirmDeleteModal from '../../../components/common/ConfirmDeleteModal';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const inp = 'w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-all';
const inpStyle = {
  borderColor: SYSTEM_COLORS.border,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};
const Label = ({ children }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
    {children}
  </label>
);

const Spinner = () => (
  <div className="flex items-center justify-center h-48">
    <div
      className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
      style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }}
    />
  </div>
);

const statusCountStyle = (type) => {
  if (type === 'available')    return { bg: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
  if (type === 'inUse')        return { bg: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info };
  if (type === 'maintenance')  return { bg: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
  return { bg: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
};

// ─── Modal ────────────────────────────────────────────────────────────────────

const HardwareModal = ({ isOpen, onClose, onSubmit, selectedHardware, formData, setFormData, images, handleImageUpload, removeImage }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92dvh] shadow-lg"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
              <Cpu className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
            </div>
            <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
              {selectedHardware ? 'Edit Hardware' : 'Add Hardware'}
            </h2>
          </div>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg hover:opacity-70" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="overflow-y-auto flex-1">
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className={inp} style={inpStyle} required />
              </div>
              <div>
                <Label>Company *</Label>
                <input type="text" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} className={inp} style={inpStyle} required />
              </div>
              <div>
                <Label>Category *</Label>
                <input type="text" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className={inp} style={inpStyle} required />
              </div>
              <div>
                <Label>Serial Number</Label>
                <input type="text" value={formData.serialNumber} onChange={e => setFormData({ ...formData, serialNumber: e.target.value })} className={inp} style={inpStyle} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'available', label: 'Available' },
                { key: 'inUse',     label: 'In Use' },
                { key: 'maintenance', label: 'Maintenance' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <input
                    type="number" min="0"
                    value={formData.status[key]}
                    onChange={e => setFormData({ ...formData, status: { ...formData.status, [key]: parseInt(e.target.value) || 0 } })}
                    className={inp} style={inpStyle}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Condition</Label>
              <select value={formData.condition} onChange={e => setFormData({ ...formData, condition: e.target.value })} className={inp} style={inpStyle}>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>

            <div>
              <Label>Description</Label>
              <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className={inp} style={inpStyle} rows={3} />
            </div>

            <div>
              <Label>Images</Label>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mt-1">
                {images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img.preview} alt={`Preview ${i + 1}`} className="w-full h-20 object-cover rounded-xl" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.danger, color: '#fff' }}>
                      <X size={11} />
                    </button>
                  </div>
                ))}
                <label className="flex flex-col items-center justify-center h-20 rounded-xl border-2 border-dashed cursor-pointer hover:opacity-70 transition-opacity" style={{ borderColor: SYSTEM_COLORS.border }}>
                  <Upload className="w-5 h-5" style={{ color: SYSTEM_COLORS.textSoft }} />
                  <span className="mt-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>Add</span>
                  <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} />
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-semibold border hover:opacity-70 transition-opacity" style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}>
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>
              {selectedHardware ? 'Update Hardware' : 'Add Hardware'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────

const defaultForm = {
  name: '', category: '', company: '', description: '',
  status: { available: 0, inUse: 0, maintenance: 0 },
  serialNumber: '', condition: 'good', images: [],
};

const HardwareSection = () => {
  const { user } = useApiAuth();
  const { hasPermission } = useAuth();
  const [hardware, setHardware]               = useState([]);
  const [isModalOpen, setIsModalOpen]         = useState(false);
  const [selectedHardware, setSelectedHardware] = useState(null);
  const [searchTerm, setSearchTerm]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('all');
  const [images, setImages]                   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [formData, setFormData]               = useState(defaultForm);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadHardware = useCallback(async () => {
    setLoading(true);
    try {
      setHardware(await resourceService.getResources('hardware'));
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load hardware items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHardware(); }, [loadHardware]);

  const handleImageUpload = (e) => {
    const newImgs = Array.from(e.target.files).map(f => ({ file: f, preview: URL.createObjectURL(f), isExisting: false }));
    setImages(prev => [...prev, ...newImgs]);
  };

  const removeImage = (i) => {
    setImages(prev => {
      const next = [...prev];
      if (!next[i].isExisting) URL.revokeObjectURL(next[i].preview);
      next.splice(i, 1);
      return next;
    });
  };

  const openAdd = () => {
    setSelectedHardware(null);
    setFormData(defaultForm);
    setImages([]);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setSelectedHardware(item);
    setFormData({ ...item, status: item.status || { available: 0, inUse: 0, maintenance: 0 } });
    setImages((item.images || []).map(url => ({ preview: url, isExisting: true })));
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const total = Object.values(formData.status).reduce((a, b) => a + b, 0);
    if (total === 0) { toast.error('Total quantity must be > 0'); return; }
    try {
      const data = { ...formData, imageFiles: images.filter(i => !i.isExisting).map(i => i.file), totalQuantity: total, userId: user.id, updatedAt: new Date().toISOString() };
      if (selectedHardware) {
        await resourceService.updateResource('hardware', selectedHardware.id, data);
        toast.success('Hardware updated');
      } else {
        await resourceService.addResource('hardware', data);
        toast.success('Hardware added');
      }
      setIsModalOpen(false);
      setImages([]);
      loadHardware();
    } catch {
      toast.error('Failed to save hardware');
    }
  };

  const handleDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await resourceService.deleteResource('hardware', deleteTarget.id);
      toast.success('Hardware deleted');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadHardware();
    } catch {
      toast.error('Failed to delete hardware');
    }
  };

  const filtered = hardware.filter(item => {
    const q = searchTerm.toLowerCase();
    const matchSearch = item.name?.toLowerCase().includes(q) || item.company?.toLowerCase().includes(q) || item.category?.toLowerCase().includes(q);
    const matchFilter = filterStatus === 'all' || (item.status?.[filterStatus] ?? 0) > 0;
    return matchSearch && matchFilter;
  });

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input type="text" placeholder="Search hardware…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle} />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} style={{ color: SYSTEM_COLORS.textSoft }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle}>
            <option value="all">All</option>
            <option value="available">Available</option>
            <option value="inUse">In Use</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
        {hasPermission('resources.view') && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap hover:opacity-90 transition-opacity" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>
            <Plus size={16} />
            Add Hardware
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? <Spinner /> : error ? (
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <p className="font-semibold text-sm" style={{ color: SYSTEM_COLORS.danger }}>Failed to load hardware</p>
          <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <Cpu className="w-8 h-8 mb-3" style={{ color: SYSTEM_COLORS.border }} />
          <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>No hardware found</p>
          <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>Try adjusting your search or filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(item => (
            <div
              key={item.id}
              className="flex rounded-xl border shadow-md overflow-hidden"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              {/* Image */}
              {item.images?.length > 0 ? (
                <div className="relative w-24 flex-shrink-0">
                  <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                  {item.images.length > 1 && (
                    <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff' }}>
                      +{item.images.length - 1}
                    </span>
                  )}
                </div>
              ) : (
                <div className="w-24 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                  <Cpu className="w-7 h-7" style={{ color: SYSTEM_COLORS.border }} />
                </div>
              )}

              {/* Body */}
              <div className="flex-1 p-3 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm truncate" style={{ color: NEUTRAL_COLORS.black }}>{item.name}</h3>
                    <p className="text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>{item.category}</p>
                    <p className="text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>{item.company}</p>
                  </div>
                  {hasPermission('resources.addHardware') && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: BRAND_COLORS.primary }}>
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDelete(item)} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: SYSTEM_COLORS.danger }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Status counts */}
                <div className="grid grid-cols-3 gap-1 mt-2">
                  {[
                    { key: 'available',   label: 'Avail' },
                    { key: 'inUse',       label: 'In Use' },
                    { key: 'maintenance', label: 'Maint' },
                  ].map(({ key, label }) => {
                    const s = statusCountStyle(key);
                    return (
                      <div key={key} className="flex flex-col items-center px-1 py-1 rounded-lg" style={{ backgroundColor: s.bg }}>
                        <span className="text-xs font-bold" style={{ color: s.color }}>{item.status?.[key] ?? 0}</span>
                        <span className="text-[10px] leading-none" style={{ color: s.color }}>{label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Meta */}
                <div className="mt-2 space-y-0.5">
                  {item.serialNumber && (
                    <div className="flex justify-between text-xs">
                      <span style={{ color: SYSTEM_COLORS.textSoft }}>S/N</span>
                      <span className="font-medium truncate ml-2" style={{ color: NEUTRAL_COLORS.black }}>{item.serialNumber}</span>
                    </div>
                  )}
                  {item.condition && (
                    <div className="flex justify-between text-xs">
                      <span style={{ color: SYSTEM_COLORS.textSoft }}>Condition</span>
                      <span className="font-medium capitalize" style={{ color: NEUTRAL_COLORS.black }}>{item.condition}</span>
                    </div>
                  )}
                </div>

                {item.description && (
                  <p className="text-xs mt-1.5 line-clamp-2" style={{ color: SYSTEM_COLORS.textMuted }}>{item.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <HardwareModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        selectedHardware={selectedHardware}
        formData={formData}
        setFormData={setFormData}
        images={images}
        handleImageUpload={handleImageUpload}
        removeImage={removeImage}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Delete Hardware"
        description={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ''}
      />
    </div>
  );
};

export default HardwareSection;
